// Omnix/client/ui/MergeContactsModal.js
import { ComponentBase } from "../core/ComponentBase.js";
import { createAvatarElement } from "../../helpers/avatarHelper.js";

export class MergeContactsModal extends ComponentBase {
    constructor(config) {
        super(config);
        this.targetContact = null;
        this.sourceContacts = new Map();
        this.searchResults = [];
        this._debouncedSearch = this.debounce(this.searchContacts.bind(this), 300);
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'modal fade';
        this.container.id = `${this.config.instanceId}-modal`;
        this.container.innerHTML = `
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Merge Contacts</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <p>Search for contacts to merge into <strong><span id="target-contact-name"></span></strong>. Data from the source contacts (like channels and tags) will be added to the target contact. The source contacts will then be deleted.</p>
                        <hr>
                        <div class="row">
                            <div class="col-md-6">
                                <h6>Search for Source Contacts</h6>
                                <input type="search" class="form-control form-control-sm" placeholder="Search by name or email...">
                                <div id="search-results-container" class="list-group mt-2" style="max-height: 250px; overflow-y: auto;"></div>
                            </div>
                            <div class="col-md-6">
                                <h6>Contacts to Merge (Sources)</h6>
                                <div id="source-contacts-container" class="list-group" style="max-height: 250px; overflow-y: auto;">
                                    <div class="list-group-item text-muted">No source contacts selected.</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" class="btn btn-primary" id="merge-btn" disabled>Merge Contacts</button>
                    </div>
                </div>
            </div>
        `;
        this.modalInstance = new bootstrap.Modal(this.container);
        this.searchInput = this.container.querySelector('input[type="search"]');
        this.searchResultsContainer = this.container.querySelector('#search-results-container');
        this.sourceContactsContainer = this.container.querySelector('#source-contacts-container');
        this.mergeButton = this.container.querySelector('#merge-btn');

        this.searchInput.addEventListener('input', (e) => this._debouncedSearch(e.target.value));
        this.mergeButton.addEventListener('click', this.handleMerge.bind(this));
        await super.initialize();
    }

    show(targetContact) {
        this.targetContact = targetContact;
        this.sourceContacts.clear();
        this.searchResults = [];
        this.searchInput.value = '';
        this.container.querySelector('#target-contact-name').textContent = `${targetContact.firstName || ''} ${targetContact.lastName || ''}`.trim();
        this.renderSourceContacts();
        this.renderSearchResults();
        this.modalInstance.show();
    }

    async searchContacts(term) {
        if (term.length < 2) {
            this.searchResults = [];
            this.renderSearchResults();
            return;
        }
        try {
            const result = await this.appClient.rpcClient.call('OmniContactsRpc.getData', {
                filter: { $or: [{ firstName: { $regex: term, $options: 'i' } }, { lastName: { $regex: term, $options: 'i' } }, { email: { $regex: term, $options: 'i' } }] },
                pageSize: 10
            });
            this.searchResults = (result.data || []).filter(c => c._id !== this.targetContact._id && !this.sourceContacts.has(c._id));
            this.renderSearchResults();
        } catch (error) {
            this.log.error("Error searching contacts for merge:", error);
        }
    }

    renderSearchResults() {
        this.searchResultsContainer.innerHTML = '';
        if (this.searchResults.length === 0) {
            this.searchResultsContainer.innerHTML = `<div class="list-group-item text-muted small">No results found.</div>`;
            return;
        }
        this.searchResults.forEach(contact => {
            const item = document.createElement('a');
            item.href = '#';
            item.className = 'list-group-item list-group-item-action py-2';
            const displayName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
            item.innerHTML = `<strong>${displayName}</strong><br><small class="text-muted">${contact.email || ''}</small>`;
            item.addEventListener('click', (e) => {
                e.preventDefault();
                this.addSourceContact(contact);
            });
            this.searchResultsContainer.appendChild(item);
        });
    }

    addSourceContact(contact) {
        this.sourceContacts.set(contact._id, contact);
        this.searchInput.value = '';
        this.searchResults = [];
        this.renderSearchResults();
        this.renderSourceContacts();
    }

    removeSourceContact(contactId) {
        this.sourceContacts.delete(contactId);
        this.renderSourceContacts();
    }

    renderSourceContacts() {
        this.sourceContactsContainer.innerHTML = '';
        if (this.sourceContacts.size === 0) {
            this.sourceContactsContainer.innerHTML = `<div class="list-group-item text-muted small">No source contacts selected.</div>`;
            this.mergeButton.disabled = true;
            return;
        }
        this.sourceContacts.forEach(contact => {
            const item = document.createElement('div');
            item.className = 'list-group-item py-2 d-flex justify-content-between align-items-center';
            const displayName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
            item.innerHTML = `<span><strong>${displayName}</strong><br><small class="text-muted">${contact.email || ''}</small></span>`;
            const removeBtn = document.createElement('button');
            removeBtn.className = 'btn btn-sm btn-outline-danger';
            removeBtn.innerHTML = `<i class="bi bi-x-lg"></i>`;
            removeBtn.addEventListener('click', () => this.removeSourceContact(contact._id));
            item.appendChild(removeBtn);
            this.sourceContactsContainer.appendChild(item);
        });
        this.mergeButton.disabled = false;
    }

    async handleMerge() {
        const sourceIds = Array.from(this.sourceContacts.keys());
        const confirmed = await this.appClient.notifier.confirm(`Are you sure you want to merge ${sourceIds.length} contact(s) into "${this.targetContact.firstName}"? This action cannot be undone.`, { type: 'danger' });
        if (confirmed) {
            try {
                await this.appClient.rpcClient.call('OmniContactsRpc.merge', {
                    targetContactId: this.targetContact._id,
                    sourceContactIds: sourceIds
                });
                this.appClient.notifier.success("Contacts merged successfully.");
                this.emit('contactsMerged');
                this.modalInstance.hide();
            } catch (error) {
                this.appClient.notifier.error(`Merge failed: ${error.message}`);
            }
        }
    }

    debounce(func, delay) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), delay);
        };
    }

    render() { return this.container; }
    destroy() { this.modalInstance?.dispose(); this.container?.remove(); super.destroy(); }
}